/**
 * Submissions Page with Advanced Filtering
 * Allows teachers to find submissions by learner, assignment, status, and date range
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip,
  TablePagination, Stack, Autocomplete, TextField, FormControl,
  InputLabel, Select, MenuItem, TableSortLabel
} from '@mui/material';
import {
  Visibility, Edit, Delete, Refresh, Home
} from '@mui/icons-material';
import axios from 'axios';

interface SchoolClass {
  id: number;
  name: string;
  subject: string;
  grade_level: number | null;
  teacher: number;
}

interface Submission {
  id: number;
  assessment_id: number;
  assessment_title: string;
  learner_id: number;
  learner_name: string;
  learner_classes: SchoolClass[];
  original_filename: string;
  status: string;
  ai_score: number | null;
  teacher_score: number | null;
  submitted_at: string;
  is_active: boolean;
}

interface Learner {
  id: number;
  first_name: string;
  last_name: string;
  student_id: string;
  email: string;
}

interface Assignment {
  id: number;
  title: string;
  due_date: string;
}

const SubmissionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialLoadComplete = useRef(false);
  const firstFetchDone = useRef(false);
  const initialFilterValues = useRef<any>(null);

  // Data state
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [learners, setLearners] = useState<Learner[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [classes, setClasses] = useState<SchoolClass[]>([]);

  // Filter state - initialize from localStorage
  const [selectedLearner, setSelectedLearner] = useState<Learner | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [selectedClass, setSelectedClass] = useState<SchoolClass | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>(() => {
    // Load saved status filter from localStorage
    return localStorage.getItem('submissionsStatusFilter') || 'all';
  });
  const [selectedGrade, setSelectedGrade] = useState<number | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Available options for filters (derived from teacher's classes)
  const [availableGrades, setAvailableGrades] = useState<number[]>([]);
  const [availableSubjects, setAvailableSubjects] = useState<string[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Sorting state
  type SortField = 'assessment' | 'learner' | 'filename' | 'status' | 'ai_score' | 'teacher_score' | 'submitted';
  const [sortBy, setSortBy] = useState<SortField | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Save filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem('submissionsStatusFilter', selectedStatus);
  }, [selectedStatus]);

  useEffect(() => {
    if (selectedLearner) {
      localStorage.setItem('submissionsLearnerFilter', JSON.stringify(selectedLearner));
    } else {
      localStorage.removeItem('submissionsLearnerFilter');
    }
  }, [selectedLearner]);

  useEffect(() => {
    if (selectedAssignment) {
      localStorage.setItem('submissionsAssignmentFilter', JSON.stringify(selectedAssignment));
    } else {
      localStorage.removeItem('submissionsAssignmentFilter');
    }
  }, [selectedAssignment]);

  useEffect(() => {
    if (selectedClass) {
      localStorage.setItem('submissionsClassFilter', JSON.stringify(selectedClass));
    } else {
      localStorage.removeItem('submissionsClassFilter');
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedGrade !== null) {
      localStorage.setItem('submissionsGradeFilter', selectedGrade.toString());
    } else {
      localStorage.removeItem('submissionsGradeFilter');
    }
  }, [selectedGrade]);

  useEffect(() => {
    if (selectedSubject) {
      localStorage.setItem('submissionsSubjectFilter', selectedSubject);
    } else {
      localStorage.removeItem('submissionsSubjectFilter');
    }
  }, [selectedSubject]);

  // Load reference data
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        // Get URL parameters first
        const urlLearnerId = searchParams.get('learner');
        const urlAssignmentId = searchParams.get('assignment');
        const urlClassId = searchParams.get('class');

        const [learnersRes, assignmentsRes, classesRes] = await Promise.all([
          axios.get('/api/learners/?page_size=1000'),
          axios.get('/api/assignments/?page_size=1000'),
          axios.get('/api/school-classes/?page_size=1000'),
        ]);

        const loadedLearners = learnersRes.data.results || [];
        const loadedAssignments = assignmentsRes.data.results || [];
        const loadedClasses = classesRes.data.results || [];

        setLearners(loadedLearners);
        setAssignments(loadedAssignments);
        setClasses(loadedClasses);

        // The backend already filters classes for the current teacher
        // So loadedClasses contains only the teacher's classes
        const teacherClasses: SchoolClass[] = loadedClasses;

        // Extract unique grade levels (only non-null values)
        const grades = Array.from(
          new Set(
            teacherClasses
              .map((c: SchoolClass) => c.grade_level)
              .filter((g): g is number => g !== null)
          )
        ).sort((a, b) => a - b);
        setAvailableGrades(grades);

        // Extract unique subjects (only non-empty values)
        const subjects = Array.from(
          new Set(
            teacherClasses
              .map((c: SchoolClass) => c.subject)
              .filter((s): s is string => {
                return typeof s === 'string' && s.trim() !== '';
              })
          )
        ).sort();
        setAvailableSubjects(subjects);

        // Apply filters: URL parameters override localStorage

        // Learner filter
        if (urlLearnerId) {
          const learnerFromUrl = loadedLearners.find((l: Learner) => l.id === parseInt(urlLearnerId, 10));
          if (learnerFromUrl) {
            setSelectedLearner(learnerFromUrl);
          }
        } else {
          const savedLearnerStr = localStorage.getItem('submissionsLearnerFilter');
          if (savedLearnerStr) {
            try {
              const savedLearner = JSON.parse(savedLearnerStr);
              const matchingLearner = loadedLearners.find((l: Learner) => l.id === savedLearner.id);
              if (matchingLearner) {
                setSelectedLearner(matchingLearner);
              }
            } catch (e) {
              localStorage.removeItem('submissionsLearnerFilter');
            }
          }
        }

        // Assignment filter
        if (urlAssignmentId) {
          const assignmentFromUrl = loadedAssignments.find((a: Assignment) => a.id === parseInt(urlAssignmentId, 10));
          if (assignmentFromUrl) {
            setSelectedAssignment(assignmentFromUrl);
          }
        } else {
          const savedAssignmentStr = localStorage.getItem('submissionsAssignmentFilter');
          if (savedAssignmentStr) {
            try {
              const savedAssignment = JSON.parse(savedAssignmentStr);
              const matchingAssignment = loadedAssignments.find((a: Assignment) => a.id === savedAssignment.id);
              if (matchingAssignment) {
                setSelectedAssignment(matchingAssignment);
              }
            } catch (e) {
              localStorage.removeItem('submissionsAssignmentFilter');
            }
          }
        }

        // Class filter
        if (urlClassId) {
          const classFromUrl = loadedClasses.find((c: SchoolClass) => c.id === parseInt(urlClassId, 10));
          if (classFromUrl) {
            setSelectedClass(classFromUrl);
          }
        } else {
          const savedClassStr = localStorage.getItem('submissionsClassFilter');
          if (savedClassStr) {
            try {
              const savedClass = JSON.parse(savedClassStr);
              const matchingClass = loadedClasses.find((c: SchoolClass) => c.id === savedClass.id);
              if (matchingClass) {
                setSelectedClass(matchingClass);
              }
            } catch (e) {
              localStorage.removeItem('submissionsClassFilter');
            }
          }
        }

        const savedGradeStr = localStorage.getItem('submissionsGradeFilter');
        if (savedGradeStr) {
          const savedGrade = parseInt(savedGradeStr, 10);
          if (!isNaN(savedGrade) && grades.includes(savedGrade)) {
            setSelectedGrade(savedGrade);
          } else {
            localStorage.removeItem('submissionsGradeFilter');
          }
        }

        const savedSubject = localStorage.getItem('submissionsSubjectFilter');
        if (savedSubject && subjects.includes(savedSubject)) {
          setSelectedSubject(savedSubject);
        } else if (savedSubject) {
          localStorage.removeItem('submissionsSubjectFilter');
        }

        // Don't call fetchSubmissions here - let it be triggered by the useEffect below
        // Just mark that initial reference data loading is complete
        initialLoadComplete.current = true;
      } catch (err: any) {
        console.error('Error loading reference data:', err);
        initialLoadComplete.current = true;
      }
    };

    fetchReferenceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Load submissions with filters
  useEffect(() => {
    // Don't fetch until reference data is loaded
    if (!initialLoadComplete.current) {
      return;
    }

    const currentFilterValues = {
      selectedLearner: selectedLearner?.id,
      selectedAssignment: selectedAssignment?.id,
      selectedClass: selectedClass?.id,
      selectedStatus,
      selectedGrade,
      selectedSubject,
      startDate,
      endDate
    };

    // First fetch
    if (!firstFetchDone.current) {
      firstFetchDone.current = true;
      initialFilterValues.current = currentFilterValues;
      fetchSubmissions();
      return;
    }

    // Check if filters have actually changed from initial values
    const filtersChanged =
      currentFilterValues.selectedLearner !== initialFilterValues.current.selectedLearner ||
      currentFilterValues.selectedAssignment !== initialFilterValues.current.selectedAssignment ||
      currentFilterValues.selectedClass !== initialFilterValues.current.selectedClass ||
      currentFilterValues.selectedStatus !== initialFilterValues.current.selectedStatus ||
      currentFilterValues.selectedGrade !== initialFilterValues.current.selectedGrade ||
      currentFilterValues.selectedSubject !== initialFilterValues.current.selectedSubject ||
      currentFilterValues.startDate !== initialFilterValues.current.startDate ||
      currentFilterValues.endDate !== initialFilterValues.current.endDate;

    // Only fetch if user actually changed a filter
    if (!filtersChanged) {
      return;
    }

    // Update initial filter values to current (user changed something)
    initialFilterValues.current = currentFilterValues;
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLearner, selectedAssignment, selectedClass, selectedStatus, selectedGrade, selectedSubject, startDate, endDate]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params
      const params: any = {
        page_size: 1000, // Get all for client-side filtering
      };

      if (selectedLearner) {
        params.learner = selectedLearner.id;
      }
      if (selectedAssignment) {
        params.assessment = selectedAssignment.id;
      }
      if (selectedStatus !== 'all') {
        params.status = selectedStatus;
      }
      if (startDate) {
        params.submitted_after = startDate;
      }
      if (endDate) {
        params.submitted_before = endDate;
      }

      const response = await axios.get('/api/submissions/', { params });
      setSubmissions(response.data.results || []);
    } catch (err: any) {
      console.error('Error loading submissions:', err);
      setError(err.response?.data?.error || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedLearner(null);
    setSelectedAssignment(null);
    setSelectedClass(null);
    setSelectedStatus('all');
    setSelectedGrade(null);
    setSelectedSubject(null);
    setStartDate('');
    setEndDate('');
    setSearchQuery('');
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) {
      return;
    }

    try {
      await axios.delete(`/api/submissions/${id}/`);
      fetchSubmissions();
    } catch (err: any) {
      console.error('Error deleting submission:', err);
      setError(err.response?.data?.detail || 'Failed to delete submission');
    }
  };

  // Get status color
  const getStatusColor = (status: string): "success" | "warning" | "error" | "info" => {
    switch (status) {
      case 'marked': return 'success';
      case 'pending': return 'warning';
      case 'review': return 'info';
      default: return 'info';
    }
  };

  // Handle sort request
  const handleSort = (field: SortField) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(field);
  };

  // Apply search, class, grade, and subject filters
  const filteredSubmissions = submissions.filter(sub => {
    // Class filter
    if (selectedClass) {
      const learnerClassIds = sub.learner_classes.map(c => c.id);
      if (!learnerClassIds.includes(selectedClass.id)) {
        return false;
      }
    }

    // Grade filter (filter by learner's classes' grade levels)
    if (selectedGrade !== null) {
      const hasMatchingGrade = sub.learner_classes.some(
        (lc: SchoolClass) => lc.grade_level === selectedGrade
      );
      if (!hasMatchingGrade) {
        return false;
      }
    }

    // Subject filter (filter by learner's classes' subjects)
    if (selectedSubject) {
      const hasMatchingSubject = sub.learner_classes.some(
        (lc: SchoolClass) => lc.subject === selectedSubject
      );
      if (!hasMatchingSubject) {
        return false;
      }
    }

    // Search filter
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      sub.learner_name.toLowerCase().includes(query) ||
      sub.assessment_title.toLowerCase().includes(query) ||
      sub.original_filename.toLowerCase().includes(query)
    );
  });

  // Helper function to extract surname from full name
  const getSurname = (fullName: string): string => {
    // Assumes format "FirstName LastName" or "FirstName MiddleName(s) LastName"
    // Returns everything after the first space (surname and middle names)
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return parts[0]; // Single name
    return parts.slice(1).join(' '); // Everything after first name
  };

  // Apply sorting
  const sortedSubmissions = [...filteredSubmissions].sort((a, b) => {
    if (!sortBy) return 0;

    let compareResult = 0;

    switch (sortBy) {
      case 'assessment':
        compareResult = a.assessment_title.localeCompare(b.assessment_title);
        break;
      case 'learner':
        // Sort by surname (last name) for alphabetical record keeping
        const surnameA = getSurname(a.learner_name);
        const surnameB = getSurname(b.learner_name);
        compareResult = surnameA.localeCompare(surnameB);
        // If surnames are the same, sort by first name
        if (compareResult === 0) {
          compareResult = a.learner_name.localeCompare(b.learner_name);
        }
        break;
      case 'filename':
        compareResult = a.original_filename.localeCompare(b.original_filename);
        break;
      case 'status':
        compareResult = a.status.localeCompare(b.status);
        break;
      case 'ai_score':
        compareResult = (a.ai_score ?? -1) - (b.ai_score ?? -1);
        break;
      case 'teacher_score':
        compareResult = (a.teacher_score ?? -1) - (b.teacher_score ?? -1);
        break;
      case 'submitted':
        compareResult = new Date(a.submitted_at).getTime() - new Date(b.submitted_at).getTime();
        break;
    }

    return sortOrder === 'asc' ? compareResult : -compareResult;
  });

  // Paginate results
  const paginatedSubmissions = sortedSubmissions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <MuiLink
            component="button"
            variant="body1"
            onClick={() => navigate('/dashboard')}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <Home fontSize="small" />
            Dashboard
          </MuiLink>
          <Typography color="text.primary">Submissions</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" gutterBottom>Submissions</Typography>
            <Typography variant="body2" color="textSecondary">
              View and manage student submissions with advanced filtering
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchSubmissions}
          >
            Refresh
          </Button>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Filters</Typography>
          <Stack spacing={2}>
            {/* Row 1: Learner, Assignment, and Class */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Autocomplete
                fullWidth
                options={learners}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.student_id})`}
                value={selectedLearner}
                onChange={(_, newValue) => setSelectedLearner(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Learner"
                    placeholder="Search by name or student ID"
                  />
                )}
              />
              <Autocomplete
                fullWidth
                options={assignments}
                getOptionLabel={(option) => option.title}
                value={selectedAssignment}
                onChange={(_, newValue) => setSelectedAssignment(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assignment"
                    placeholder="Search by assignment title"
                  />
                )}
              />
              <Autocomplete
                fullWidth
                options={classes}
                getOptionLabel={(option) => option.name}
                value={selectedClass}
                onChange={(_, newValue) => setSelectedClass(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Class"
                    placeholder="Search by class name"
                  />
                )}
              />
            </Stack>

            {/* Row 1.5: Grade and Subject */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Grade Level</InputLabel>
                <Select
                  value={selectedGrade === null ? '' : String(selectedGrade)}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedGrade(value === '' ? null : Number(value));
                  }}
                  label="Grade Level"
                >
                  <MenuItem value="">All Grades</MenuItem>
                  {availableGrades.map((grade) => (
                    <MenuItem key={grade} value={String(grade)}>
                      Grade {grade}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={selectedSubject || ''}
                  onChange={(e) => setSelectedSubject(e.target.value === '' ? null : e.target.value)}
                  label="Subject"
                >
                  <MenuItem value="">All Subjects</MenuItem>
                  {availableSubjects.map((subject) => (
                    <MenuItem key={subject} value={subject}>
                      {subject}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Row 2: Status and Date Range */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="marked">Marked</MenuItem>
                  <MenuItem value="review">Review</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                type="date"
                label="Submitted From"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                type="date"
                label="Submitted To"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            {/* Row 3: Search Box */}
            <TextField
              fullWidth
              label="Search"
              placeholder="Search by learner name, assignment, or filename..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            {/* Reset Button */}
            <Box>
              <Button variant="outlined" onClick={handleReset}>
                Reset Filters
              </Button>
            </Box>
          </Stack>

          {/* Results Count */}
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              Showing {sortedSubmissions.length} submission{sortedSubmissions.length !== 1 ? 's' : ''}
              {(selectedLearner || selectedAssignment || selectedClass || selectedStatus !== 'all' || selectedGrade !== null || selectedSubject || startDate || endDate || searchQuery) && ' (filtered)'}
            </Typography>
          </Box>
        </Paper>

        {/* Submissions Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={60}>#</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'assessment'}
                        direction={sortBy === 'assessment' ? sortOrder : 'asc'}
                        onClick={() => handleSort('assessment')}
                      >
                        Assessment
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'learner'}
                        direction={sortBy === 'learner' ? sortOrder : 'asc'}
                        onClick={() => handleSort('learner')}
                      >
                        Learner
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'filename'}
                        direction={sortBy === 'filename' ? sortOrder : 'asc'}
                        onClick={() => handleSort('filename')}
                      >
                        File Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'status'}
                        direction={sortBy === 'status' ? sortOrder : 'asc'}
                        onClick={() => handleSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === 'ai_score'}
                        direction={sortBy === 'ai_score' ? sortOrder : 'asc'}
                        onClick={() => handleSort('ai_score')}
                      >
                        AI Score
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === 'teacher_score'}
                        direction={sortBy === 'teacher_score' ? sortOrder : 'asc'}
                        onClick={() => handleSort('teacher_score')}
                      >
                        Teacher Score
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'submitted'}
                        direction={sortBy === 'submitted' ? sortOrder : 'asc'}
                        onClick={() => handleSort('submitted')}
                      >
                        Submitted
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSubmissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography variant="body2" color="textSecondary" py={4}>
                          No submissions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedSubmissions.map((submission, index) => (
                      <TableRow key={submission.id} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{submission.assessment_title}</TableCell>
                        <TableCell>{submission.learner_name}</TableCell>
                        <TableCell>{submission.original_filename}</TableCell>
                        <TableCell>
                          <Chip
                            label={submission.status}
                            size="small"
                            color={getStatusColor(submission.status)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {submission.ai_score !== null ? Number(submission.ai_score).toFixed(1) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {submission.teacher_score !== null ? Number(submission.teacher_score).toFixed(1) : '-'}
                        </TableCell>
                        <TableCell>
                          {submission.submitted_at ? new Date(submission.submitted_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => {
                                // Build URL with current filter context for seamless navigation
                                const params = new URLSearchParams();
                                if (selectedClass) params.set('class', selectedClass.id.toString());
                                if (selectedAssignment) params.set('assignment', selectedAssignment.id.toString());
                                if (selectedLearner) params.set('learner', selectedLearner.id.toString());
                                if (selectedStatus && selectedStatus !== 'all') params.set('status', selectedStatus);
                                navigate(`/submissions/${submission.id}/mark?${params.toString()}`);
                              }}
                              startIcon={<Visibility fontSize="small" />}
                            >
                              Mark
                            </Button>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/submissions/${submission.id}/edit`)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(submission.id)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {sortedSubmissions.length > 0 && (
              <TablePagination
                component="div"
                count={sortedSubmissions.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            )}
          </Paper>
        )}
      </Box>
  );
};

export default SubmissionsPage;
